<script setup lang="ts">
import { computed, reactive } from "vue";
import { isMobileView } from "@persolog/utils";
import UserDropdown from "./UserDropdown.vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import AppHeader from "@persolog/ui/AppHeader.vue";
import LanguageSelection from "@persolog/ui/LanguageSelection.vue";
import ThemeSwitcher from "@persolog/ui/ThemeSwitcher.vue";
import { useTheme } from "../composables/useTheme";
import { getSections } from "../config/sections";

const { t, locale } = useI18n({ useScope: "global" });
const route = useRoute();
const { theme, toggleTheme } = useTheme();

const onLanguageChange = (lang: string) => {
    locale.value = lang;
    localStorage.setItem("selectedLanguage", lang);
};

const props = defineProps({
    highlight: {
        type: String,
        default: "",
    },
});

// The header is mounted once in App.vue, so no view passes a highlight: the current route is the
// current page. Each item's id is its route; section headers get none, so only a page is ever marked
// and a section's dropdown is never disabled.
const currentPage = computed(() => props.highlight || route.path);

// Built from the shared section config so the nav dropdowns and the per-team overview pages can't
// drift. Group headers are the section titles; tool labels reuse their header.navBar.* i18n keys.
const items = reactive([
    {
        label: computed(() => t("header.navBar.start")),
        icon: "pi pi-fw pi-home",
        route: "/",
        id: "/",
    },
    ...getSections().map((section) => ({
        label: section.title,
        icon: section.icon,
        items: section.tools.map((tool) => ({
            label: tool.labelKey ? computed(() => t(tool.labelKey as string)) : (tool.label ?? ""),
            icon: tool.icon,
            route: tool.to,
            id: tool.to,
        })),
    })),
]);
</script>

<template>
    <AppHeader :items="items" :highlight="currentPage">
        <template #start>
            <a href="/" v-if="!isMobileView()">
                <img alt="logo" src="../assets/persolog-logo.svg" height="40" class="h-10 w-auto mr-6" />
            </a>
        </template>
        <template #end>
            <div class="flex justify-between items-center">
                <a class="ml-2" href="/" v-if="isMobileView()">
                    <img alt="logo" src="../assets/persolog-logo.svg" height="24" class="h-6 w-auto mr-2 mt-2" />
                </a>
                <div>
                    <div class="user-controls-container">
                        <UserDropdown />
                        <div class="theme-switcher-wrapper">
                            <ThemeSwitcher :is-high-contrast="theme === 'high-contrast'" @toggle="toggleTheme" />
                        </div>
                        <div class="language-selector-wrapper">
                            <LanguageSelection @change="onLanguageChange" />
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </AppHeader>
</template>

<style scoped>
.user-controls-container {
    display: flex;
    align-items: center;
    position: relative;
}

.theme-switcher-wrapper {
    position: relative;
    margin-left: 1.5rem;
    margin-right: 1rem;
}

@media screen and (max-width: 768px) {
    .theme-switcher-wrapper {
        margin-left: 1rem;
        margin-right: 0.5rem;
    }
}

.language-selector-wrapper {
    position: relative;
    margin-left: 1.5rem;
    margin-right: 1rem;
    margin-bottom: auto;
    margin-top: auto;
}

@media screen and (max-width: 768px) {
    .language-selector-wrapper {
        margin-left: 1rem;
        margin-bottom: auto;
        margin-top: auto;
        margin-right: 2rem;
    }
}
</style>
